import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Mail } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SKIDDLE_URL = "https://www.skiddle.com/whats-on/Sheffield/The-Viper-Rooms/Murkz-Presents---The-Shout-Boxing-Day-Special/43028604/";
const INSTAGRAM_URL = "https://www.instagram.com/murkzpresents";

const track = (type, target = "", value = 0, keepalive = false) => {
  try {
    let visitor = "new";
    try {
      visitor = localStorage.getItem("mp_v") ? "returning" : "new";
      localStorage.setItem("mp_v", "1");
    } catch {}
    const payload = {
      type,
      target,
      ref: document.referrer || "",
      device: window.innerWidth < 768 ? "mobile" : "desktop",
      visitor,
      value,
    };
    if (keepalive) {
      fetch(`${API}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } else {
      axios.post(`${API}/track`, payload);
    }
  } catch {}
};

const ARTISTS = [
  "Trilla", "Murkz", "Subzero", "Pantha", "1st Born", "Apostle", "Teresa (Live PA)",
  "Nev Wright", "Freddo", "Dyno", "Mala P", "Rawns", "DJ Marls",
  "DJ Mxnic", "DJ Law", "Meadz", "Lace Moss", "Ri Ri",
];

const Nav = () => {
  const [open, setOpen] = useState(false);

  const anchors = [
    { id: "tickets", label: "tickets", href: "#tickets" },
    { id: "follow", label: "follow", href: "#follow" },
    { id: "signup", label: "sign up", href: "#signup" },
  ];

  return (
    <header className="sticky top-0 z-50">
      <nav
        data-testid="site-nav"
        className="flex items-stretch justify-between bg-[#f4f4f0] border-b-4 border-[#0f0f0f]"
      >
        <a
          data-testid="nav-brand"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="font-display lowercase tracking-tighter text-2xl md:text-3xl px-4 md:px-8 py-3 hover:text-[#f5341c] transition-colors duration-150"
        >
          murkz presents
        </a>
        <button
          data-testid="nav-menu-button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          className="flex items-center gap-3 px-5 md:px-8 text-sm font-bold uppercase tracking-widest bg-[#f5341c] border-l-4 border-[#0f0f0f] hover:bg-[#0f0f0f] hover:text-[#f4f4f0] transition-colors duration-150"
        >
          {open ? "close" : "menu"}
          <span className="flex flex-col gap-1" aria-hidden="true">
            <span className={`block w-5 h-[3px] bg-current transition-transform duration-150 ${open ? "rotate-45 translate-y-[7px]" : ""}`} />
            <span className={`block w-5 h-[3px] bg-current transition-opacity duration-150 ${open ? "opacity-0" : ""}`} />
            <span className={`block w-5 h-[3px] bg-current transition-transform duration-150 ${open ? "-rotate-45 -translate-y-[7px]" : ""}`} />
          </span>
        </button>
      </nav>
      {open && (
        <div data-testid="nav-menu" className="bg-[#f4f4f0] border-b-4 border-[#0f0f0f]">
          {anchors.map((l) => (
            <a
              key={l.id}
              data-testid={`nav-menu-${l.id}-link`}
              href={l.href}
              onClick={() => {
                setOpen(false);
                if (l.id === "tickets") track("click", "buy-tickets-menu");
              }}
              className="block font-display lowercase tracking-tight text-4xl md:text-5xl px-4 md:px-8 py-2 md:py-3 border-t-2 border-[#0f0f0f] hover:bg-[#f5341c] transition-colors duration-150"
            >
              {l.label}
            </a>
          ))}
          <Link
            data-testid="nav-menu-privacy-link"
            to="/privacy"
            onClick={() => setOpen(false)}
            className="block font-display lowercase tracking-tight text-4xl md:text-5xl px-4 md:px-8 py-2 md:py-3 border-t-2 border-[#0f0f0f] hover:bg-[#f5341c] transition-colors duration-150"
          >
            privacy policy
          </Link>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <header data-testid="hero-section" className="relative overflow-hidden border-b-4 border-[#0f0f0f]">
    <video
      data-testid="hero-bg-video"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ objectPosition: "50% 50%" }}
      poster="/hero-poster.jpg"
      preload="auto"
      autoPlay
      muted
      loop
      playsInline
      ref={(v) => { if (v) v.play().catch(() => {}); }}
    >
      <source src="/hero-bg.webm" type="video/webm" />
      <source src="/hero-bg.mp4" type="video/mp4" />
    </video>
    <div className="absolute inset-0 bg-[#0f0f0f]/45" aria-hidden="true" />

    <div className="relative px-4 md:px-8 pt-8 md:pt-10 pb-6 md:pb-8">
      <p
        data-testid="hero-meta"
        className="md:absolute md:right-8 md:top-8 text-sm md:text-base font-extrabold uppercase tracking-widest leading-tight mb-4 md:mb-0 text-[#f4f4f0]"
      >
        Boxing
        <br />
        Day Special
        <br />
        2200 - Late
      </p>
      <h1
        data-testid="hero-brand"
        className="font-display lowercase leading-[0.82] tracking-[-0.03em] text-[13vw] md:text-[10.5vw] text-[#f4f4f0]"
      >
        murkz presents
      </h1>
      <h2
        data-testid="hero-title"
        className="font-display lowercase leading-[0.82] tracking-[-0.03em] text-[13vw] md:text-[10.5vw] text-[#f5341c] mt-2 md:mt-3"
      >
        the shout
      </h2>
      <div className="mt-5 md:mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6">
        <p
          data-testid="hero-location"
          className="text-sm md:text-base font-extrabold uppercase tracking-widest text-[#f4f4f0]"
        >
          Viper Rooms
          <br />
          Sheffield
        </p>
        <a
          data-testid="hero-tickets-button"
          href="#tickets"
          onClick={() => track("click", "buy-tickets-hero")}
          className="inline-block self-start bg-[#46b795] text-[#0f0f0f] font-display lowercase tracking-tight text-2xl md:text-3xl px-8 md:px-10 py-2 md:py-3 border-4 border-[#0f0f0f] hover:bg-[#f4f4f0] transition-colors duration-150"
        >
          buy tickets
        </a>
      </div>
    </div>
  </header>
);

const Lineup = () => (
  <section data-testid="lineup-section" className="bg-[#46b795] border-b-4 border-[#0f0f0f] px-4 md:px-8 py-8 md:py-12">
    <p className="text-sm font-extrabold uppercase tracking-widest mb-6">Full line-up</p>
    <p
      data-testid="lineup-list"
      className="font-display lowercase leading-[1.02] tracking-tight text-3xl md:text-6xl max-w-6xl"
    >
      <span aria-hidden="true" className="text-[#f4f4f0]">⌐ </span>
      w/{" "}
      {ARTISTS.map((artist, i) => (
        <span key={artist} data-testid={`lineup-artist-${i}`}>
          <span className={i < 6 ? "text-[#f4f4f0]" : ""}>{artist}</span>
          {i < ARTISTS.length - 1 ? ", " : " "}
        </span>
      ))}
      <span aria-hidden="true" className="text-[#f4f4f0]">¬</span>
    </p>
  </section>
);

const SkiddleTicketBox = () => {
  useEffect(() => {
    if (window.__skiddleLoaded) return;
    window.__skiddleLoaded = true;
    const po = document.createElement("script");
    po.type = "text/javascript";
    po.async = true;
    po.src = "https://www.skiddle.com/infofeed/ticketpage.php?ilid=43028604&type=embedded";
    const target = document.getElementById("ticketbox_ph_43028604");
    if (target) target.appendChild(po);
  }, []);

  return (
    <div
      data-testid="skiddle-ticket-box"
      className="paper border-4 border-[#0f0f0f] p-4 md:p-8"
    >
      <p className="text-sm font-extrabold uppercase tracking-widest mb-4">
        Murkz Presents — The Shout: Boxing Day Special
      </p>
      <div id="ticketbox_ph_43028604" className="ticketbox min-h-[120px]">
        <p className="font-display lowercase tracking-tight text-2xl md:text-3xl">
          loading tickets…{" "}
          <a
            href={SKIDDLE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-[#f5341c] decoration-4"
          >
            or buy direct on skiddle
          </a>
        </p>
      </div>
      <p className="mt-4 text-xs font-bold uppercase tracking-widest text-[#0f0f0f]/60">
        <a
          href="https://www.skiddle.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[#f5341c] transition-colors duration-150"
        >
          Ticket sales powered by Skiddle
        </a>
      </p>
    </div>
  );
};

const UrgencyBanner = () => (
  <div
    data-testid="tickets-urgency-banner"
    className="bg-[#f4f4f0] text-[#0f0f0f] overflow-hidden py-2 md:py-3"
    aria-label="First release tickets sold out - limited 2nd release on sale"
  >
    <div className="marquee-track flex whitespace-nowrap font-display lowercase tracking-tight text-xl md:text-3xl">
      {[0, 1].map((n) => (
        <span key={n} aria-hidden={n === 1} className="pr-8">
          {"first release tickets sold out - limited 2nd release on sale - ".repeat(4)}
        </span>
      ))}
    </div>
  </div>
);

const REVIEW_COUNT = 7;

const GALLERY_COUNT = 6;

const Gallery = () => {
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.querySelector("[data-slide]");
    if (!slide) return;
    const i = Math.round(el.scrollLeft / (slide.offsetWidth + 12));
    setIndex(Math.min(Math.max(i, 0), GALLERY_COUNT - 1));
  };

  const onPointerDown = (e) => {
    const el = trackRef.current;
    if (!el || e.pointerType === "touch") return;
    drag.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft };
  };

  const onPointerMove = (e) => {
    const el = trackRef.current;
    if (!el || !drag.current.down) return;
    el.scrollLeft = drag.current.startScroll - (e.clientX - drag.current.startX);
  };

  const endDrag = () => {
    drag.current.down = false;
  };

  return (
    <section data-testid="gallery-section" className="bg-[#0f0f0f] border-b-4 border-[#0f0f0f] py-14 md:py-20">
      <p className="font-display lowercase tracking-tight text-3xl md:text-5xl mb-8 text-center text-[#f4f4f0] px-4">
        this is murkz presents<span className="text-[#f5341c]">.</span>
      </p>
      <div
        ref={trackRef}
        onScroll={onScroll}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        data-testid="gallery-track"
        className="scrollbar-none flex gap-3 overflow-x-auto snap-x snap-mandatory px-[9vw] sm:px-[22.5vw] md:px-[31vw] cursor-grab active:cursor-grabbing select-none"
      >
        {Array.from({ length: GALLERY_COUNT }).map((_, n) => (
          <div key={n} data-slide className="snap-center shrink-0 w-[82vw] sm:w-[55vw] md:w-[38vw]">
            <img
              data-testid={n === index ? "gallery-image" : undefined}
              src={`/gallery/gallery-${n + 1}.jpg`}
              alt={`Murkz Presents crowd photo ${n + 1} of ${GALLERY_COUNT}`}
              loading={n === 0 ? "eager" : "lazy"}
              decoding="async"
              draggable={false}
              className="w-full aspect-[4/5] object-cover shadow-[10px_10px_0_0_#f5341c]"
            />
          </div>
        ))}
      </div>
      <div className="mt-6 flex items-center justify-center gap-2">
        {Array.from({ length: GALLERY_COUNT }).map((_, n) => (
          <span
            key={n}
            data-testid={`gallery-dot-${n}`}
            className={`h-1.5 transition-all duration-200 ${n === index ? "w-6 bg-[#f5341c]" : "w-1.5 bg-[#f4f4f0]/30"}`}
          />
        ))}
      </div>
    </section>
  );
};

const Reviews = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % REVIEW_COUNT), 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <section data-testid="reviews-section" className="bg-[#46b795] border-b-4 border-[#0f0f0f] px-4 md:px-8 py-14 md:py-20">
      <p className="font-display lowercase tracking-tight text-3xl md:text-5xl mb-6 text-center">
        word from the dance<span className="text-[#f5341c]">.</span>
      </p>
      <div className="relative max-w-4xl mx-auto">
        <img
          data-testid="review-image"
          src={`/reviews/review-${index + 1}.jpg`}
          alt={`Crowd review ${index + 1} of ${REVIEW_COUNT}`}
          className="w-full border-4 border-[#0f0f0f] shadow-[10px_10px_0_0_#0f0f0f]"
        />
        <button
          data-testid="review-prev-button"
          aria-label="Previous review"
          onClick={() => setIndex((index - 1 + REVIEW_COUNT) % REVIEW_COUNT)}
          className="absolute left-2 top-1/2 -translate-y-1/2 bg-[#0f0f0f] text-[#f4f4f0] w-10 h-10 md:w-12 md:h-12 font-display text-2xl leading-none hover:bg-[#f5341c] hover:text-[#0f0f0f] transition-colors duration-150"
        >
          ‹
        </button>
        <button
          data-testid="review-next-button"
          aria-label="Next review"
          onClick={() => setIndex((index + 1) % REVIEW_COUNT)}
          className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#0f0f0f] text-[#f4f4f0] w-10 h-10 md:w-12 md:h-12 font-display text-2xl leading-none hover:bg-[#f5341c] hover:text-[#0f0f0f] transition-colors duration-150"
        >
          ›
        </button>
      </div>
      <div className="mt-4 flex justify-center gap-2">
        {Array.from({ length: REVIEW_COUNT }).map((_, n) => (
          <button
            key={n}
            data-testid={`review-dot-${n}`}
            aria-label={`Go to review ${n + 1}`}
            onClick={() => setIndex(n)}
            className={`w-3 h-3 border-2 border-[#0f0f0f] ${n === index ? "bg-[#f5341c]" : "bg-transparent"}`}
          />
        ))}
      </div>
    </section>
  );
};

const Tickets = () => (
  <section
    id="tickets"
    data-testid="tickets-section"
    className="plus-field relative border-b-4 border-[#0f0f0f]"
  >
    <div className="checker-edge absolute left-0 top-0 bottom-0 w-[22px] hidden md:block" aria-hidden="true" />
    <div className="px-4 md:px-8 py-14 md:py-24 text-center">
      <h2 className="font-display lowercase leading-[0.85] tracking-[-0.03em] text-[14vw] md:text-[9vw] text-[#0f0f0f] pb-1 md:pb-3">
        buy tickets
      </h2>
      <div className="mt-4 md:mt-6 max-w-3xl mx-auto text-left">
        <SkiddleTicketBox />
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
        <span className="relative inline-block">
          <a
            data-testid="tickets-tables-button"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("click", "dm-tables")}
            className="inline-block bg-[#0f0f0f] text-[#f4f4f0] font-display lowercase tracking-tight text-2xl md:text-4xl px-8 md:px-12 py-3 md:py-4 border-4 border-[#0f0f0f] hover:bg-[#f4f4f0] hover:text-[#0f0f0f] transition-colors duration-150"
          >
            dm for table packages
          </a>
          <span
            data-testid="tickets-callout"
            className="absolute -top-4 -right-3 bg-[#f4f4f0] text-[#0f0f0f] border-2 border-[#0f0f0f] px-2 py-1 text-xs font-extrabold uppercase tracking-widest rotate-2"
          >
            limited tables available
          </span>
        </span>
      </div>
    </div>
  </section>
);

const InstagramFeed = () => {
  const [state, setState] = useState({ loading: true, configured: false, items: [] });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/instagram/feed?limit=8`);
        setState({ loading: false, configured: res.data.configured, items: res.data.items });
      } catch {
        setState({ loading: false, configured: false, items: [] });
      }
    })();
  }, []);

  return (
    <section id="follow" data-testid="instagram-feed-section" className="paper px-4 md:px-8 py-14 md:py-24">
      <div className="flex items-end justify-between gap-6 mb-8">
        <h2 className="font-display lowercase leading-[0.85] tracking-[-0.02em] text-5xl md:text-8xl">
          follow<span className="text-[#f5341c]">.</span>
        </h2>
        <a
          data-testid="instagram-follow-button"
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("click", "instagram")}
          className="shrink-0 bg-[#0f0f0f] text-[#f4f4f0] text-sm font-bold uppercase tracking-widest px-5 py-3 border-4 border-[#0f0f0f] hover:bg-[#f5341c] hover:text-[#0f0f0f] hover:border-[#f5341c] transition-colors duration-150"
        >
          @murkzpresents
        </a>
      </div>
      {state.items.length > 0 ? (
        <div data-testid="instagram-grid" className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {state.items.map((item, i) => (
            <a
              key={item.ig_id}
              data-testid={`instagram-item-${i}`}
              href={item.permalink || INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="relative block aspect-square border-4 border-[#0f0f0f] overflow-hidden group"
            >
              {item.display_url && (
                <img
                  src={item.display_url}
                  alt={(item.caption || "murkzpresents on Instagram").slice(0, 120)}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              )}
              {item.media_type === "VIDEO" && (
                <span className="absolute bottom-2 left-2 bg-[#f5341c] text-[#0f0f0f] text-xs font-extrabold uppercase tracking-widest px-2 py-1">
                  video
                </span>
              )}
            </a>
          ))}
        </div>
      ) : (
        <a
          data-testid="instagram-fallback-link"
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="block border-4 border-[#0f0f0f] px-6 py-10 md:py-14 text-center font-display lowercase tracking-tight text-3xl md:text-5xl hover:bg-[#f5341c] transition-colors duration-150"
        >
          {state.loading ? "loading the feed…" : "latest photos & videos on instagram →"}
        </a>
      )}
    </section>
  );
};

const Signup = () => {
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [optIn, setOptIn] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!optIn) {
      toast.error("Tick the box to confirm you want to sign up.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/signup`, { email, city });
      track("click", "signup");
      toast.success("You're on the list. Big things incoming.");
      setEmail("");
      setCity("");
    } catch (err) {
      toast.error("That didn't go through — check your email and city, then try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="signup"
      data-testid="signup-section"
      className="plus-field-dark border-b-4 border-[#0f0f0f] px-4 md:px-8 py-14 md:py-24 text-[#f4f4f0]"
    >
      <h2 className="font-display lowercase leading-[1.05] md:leading-[0.9] tracking-[-0.02em] text-4xl md:text-8xl max-w-5xl">
        sign up for exclusive{" "}
        <span className="text-[#f5341c]">news & updates</span>
      </h2>
      <form
        data-testid="signup-form"
        onSubmit={handleSubmit}
        className="mt-10 flex flex-col md:flex-row md:flex-wrap gap-4 max-w-3xl"
      >
        <input
          data-testid="signup-email-input"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="EMAIL"
          className="flex-1 bg-white border-4 border-[#f4f4f0] px-4 py-3 text-base font-bold uppercase tracking-widest placeholder-[#0f0f0f]/40 text-[#0f0f0f] focus:outline-none focus:border-[#f5341c] rounded-none"
        />
        <input
          data-testid="signup-city-input"
          type="text"
          name="address-level2"
          autoComplete="address-level2"
          required
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="CITY"
          className="md:w-56 bg-white border-4 border-[#f4f4f0] px-4 py-3 text-base font-bold uppercase tracking-widest placeholder-[#0f0f0f]/40 text-[#0f0f0f] focus:outline-none focus:border-[#f5341c] rounded-none"
        />
        <button
          data-testid="signup-submit-button"
          type="submit"
          disabled={loading}
          className="bg-[#f5341c] text-[#0f0f0f] font-display lowercase tracking-tight text-2xl px-8 py-3 border-4 border-[#f5341c] hover:bg-[#f4f4f0] hover:border-[#f4f4f0] transition-colors duration-150 disabled:opacity-60"
        >
          {loading ? "…" : "click to sign up."}
        </button>
        <label
          data-testid="signup-optin-label"
          className="flex items-center gap-3 text-xs md:text-sm font-bold uppercase tracking-widest cursor-pointer md:basis-full"
        >
          <input
            data-testid="signup-optin-checkbox"
            type="checkbox"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            className="w-5 h-5 shrink-0 accent-[#f5341c]"
          />
          i want to sign up
        </label>
      </form>
    </section>
  );
};

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-[#0f0f0f] text-[#f4f4f0] px-4 md:px-8 py-10 md:py-14"
  >
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
      <p className="font-display lowercase tracking-tighter leading-[0.85] text-[14vw] md:text-[8vw]">
        murkz presents
      </p>
      <div className="flex flex-col gap-2 text-sm font-extrabold uppercase tracking-widest">
        <a
          data-testid="footer-instagram-link"
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:text-[#f5341c] transition-colors duration-150"
        >
          follow — @murkzpresents
        </a>
        <a
          data-testid="footer-email-link"
          href="mailto:contact@murkzpresents.com"
          className="flex items-center gap-2 hover:text-[#f5341c] transition-colors duration-150"
        >
          <Mail className="w-4 h-4 shrink-0" aria-hidden="true" />
          contact@murkzpresents.com
        </a>
        <span data-testid="footer-url">Murkz Presents. All rights reserved.</span>
        <Link
          data-testid="footer-privacy-link"
          to="/privacy"
          className="hover:text-[#f5341c] transition-colors duration-150"
        >
          privacy policy
        </Link>
      </div>
    </div>
  </footer>
);

const StickyCta = () => (
  <div
    data-testid="sticky-cta-bar"
    className="fixed bottom-0 inset-x-0 z-40 bg-[#f4f4f0] border-t-4 border-[#0f0f0f] flex items-center justify-between gap-3 px-4 md:px-8 py-2"
  >
    <div className="leading-tight">
      <p className="font-display lowercase tracking-tight text-base md:text-xl">murkz presents.</p>
      <p className="text-[10px] md:text-xs font-extrabold uppercase tracking-widest">
        the shout: Boxing Day special
      </p>
    </div>
    <span className="relative inline-block shrink-0">
      <a
        data-testid="sticky-cta-tickets-button"
        href="#tickets"
        onClick={() => track("click", "buy-tickets-sticky")}
        className="inline-block bg-[#f5341c] text-[#0f0f0f] font-display lowercase tracking-tight text-lg md:text-2xl px-5 md:px-8 py-1.5 border-4 border-[#0f0f0f] hover:bg-[#0f0f0f] hover:text-[#f4f4f0] transition-colors duration-150"
      >
        tickets
      </a>
      <span
        data-testid="sticky-cta-urgency-badge"
        className="absolute right-0 bottom-full mb-1 whitespace-nowrap bg-[#0f0f0f] text-[#f4f4f0] border-2 border-[#f4f4f0] px-2 py-1 text-[10px] md:text-xs font-extrabold uppercase tracking-widest rotate-2"
      >
        limited 2nd release on sale
      </span>
    </span>
  </div>
);

const Home = () => {
  useEffect(() => {
    track("pageview");

    // Ticket-box engagement: browsers fire window blur when a visitor clicks into the Skiddle iframe
    const onBlur = () => {
      const active = document.activeElement;
      const box = document.getElementById("ticketbox_ph_43028604");
      if (active && active.tagName === "IFRAME" && box && box.contains(active)) {
        if (!sessionStorage.getItem("mp_tb")) {
          sessionStorage.setItem("mp_tb", "1");
          track("click", "ticket-box-engaged");
        }
      }
    };
    window.addEventListener("blur", onBlur);

    // Section view + dwell time on the tickets section
    let enteredAt = null;
    const sendDwell = (keepalive) => {
      if (!enteredAt) return;
      const secs = Math.round((Date.now() - enteredAt) / 1000);
      enteredAt = null;
      if (secs >= 2) track("view", "tickets-time", secs, keepalive);
    };
    const onHide = () => sendDwell(true);

    const el = document.querySelector('[data-testid="tickets-section"]');
    let obs = null;
    if (el && "IntersectionObserver" in window) {
      obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              if (!sessionStorage.getItem("mp_tv")) {
                sessionStorage.setItem("mp_tv", "1");
                track("view", "tickets-section");
              }
              enteredAt = Date.now();
            } else {
              sendDwell(false);
            }
          });
        },
        { threshold: 0.4 }
      );
      obs.observe(el);
    }
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onHide);

    return () => {
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onHide);
      if (obs) obs.disconnect();
    };
  }, []);

  return (
    <main data-testid="home-page" className="min-h-screen pb-24 text-[#0f0f0f]">
      <Nav />
      <Hero />
      <Lineup />
      <UrgencyBanner />
      <Gallery />
      <Tickets />
      <Reviews />
      <InstagramFeed />
      <Signup />
      <Footer />
      <StickyCta />
    </main>
  );
};

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="privacy-page" className="min-h-screen paper text-[#0f0f0f]">
      <nav className="flex items-stretch justify-between border-b-4 border-[#0f0f0f]">
        <Link
          data-testid="privacy-back-link"
          to="/"
          className="font-display lowercase tracking-tighter text-2xl md:text-3xl px-4 md:px-8 py-3 hover:text-[#f5341c] transition-colors duration-150"
        >
          ← murkz presents
        </Link>
        <a
          data-testid="privacy-tickets-link"
          href="/#tickets"
          onClick={() => track("click", "buy-tickets-privacy")}
          className="flex items-center px-5 md:px-8 text-sm font-bold uppercase tracking-widest bg-[#f5341c] border-l-4 border-[#0f0f0f] hover:bg-[#0f0f0f] hover:text-[#f4f4f0] transition-colors duration-150"
        >
          tickets
        </a>
      </nav>
      <div className="px-4 md:px-8 py-14 md:py-24 max-w-3xl">
        <h1 className="font-display lowercase leading-[0.85] tracking-[-0.02em] text-5xl md:text-8xl">
          privacy policy<span className="text-[#f5341c]">.</span>
        </h1>
        <div className="mt-10 space-y-8 text-base md:text-lg font-medium">
          <section>
            <h2 className="text-sm font-extrabold uppercase tracking-widest mb-2">Who we are</h2>
            <p>
              Murkz Presents ("we", "us") runs club nights and events across the UK.
              This policy explains what we collect through this website and what we do with it.
            </p>
          </section>
          <section>
            <h2 className="text-sm font-extrabold uppercase tracking-widest mb-2">What we collect</h2>
            <p>
              If you join our mailing list we store your email address and city so we can
              send you news, line-up announcements and ticket releases for events in your
              area. We don't collect anything else directly, and we never sell your data.
            </p>
          </section>
          <section>
            <h2 className="text-sm font-extrabold uppercase tracking-widest mb-2">Tickets</h2>
            <p>
              Ticket sales are handled by Skiddle. When you buy a ticket, your payment and
              personal details are processed by Skiddle under their own privacy policy,
              which you can read at skiddle.com. We never see or store your payment details.
            </p>
          </section>
          <section>
            <h2 className="text-sm font-extrabold uppercase tracking-widest mb-2">Third parties</h2>
            <p>
              This site embeds content from Skiddle and links to Instagram. These services
              may set their own cookies and collect usage data according to their own
              policies. We don't run advertising trackers on this site.
            </p>
          </section>
          <section>
            <h2 className="text-sm font-extrabold uppercase tracking-widest mb-2">Your rights</h2>
            <p>
              Under UK GDPR you can ask to see, correct or delete the data we hold about
              you, or unsubscribe from the mailing list at any time. Just message us on
              Instagram{" "}
              <a
                data-testid="privacy-instagram-link"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold underline decoration-[#f5341c] decoration-4"
              >
                @murkzpresents
              </a>{" "}
              and we'll sort it.
            </p>
          </section>
        </div>
      </div>
    </main>
  );
};

const Stats = () => {
  const [key, setKey] = useState(sessionStorage.getItem("statsKey") || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async (k) => {
    try {
      const res = await axios.get(`${API}/stats?key=${encodeURIComponent(k)}`);
      setData(res.data);
      sessionStorage.setItem("statsKey", k);
      setErr("");
    } catch {
      setErr("that key didn't work — check it and try again");
      setData(null);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const days = [];
  if (data) {
    const today = new Date();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const k = d.toISOString().slice(0, 10);
      days.push([
        d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
        data.pageviews_by_day[k] || 0,
      ]);
    }
  }
  const maxDay = days.length ? Math.max(...days.map(([, v]) => v), 1) : 1;

  return (
    <main data-testid="stats-page" className="min-h-screen paper text-[#0f0f0f]">
      <nav className="flex items-stretch justify-between border-b-4 border-[#0f0f0f]">
        <Link
          data-testid="stats-back-link"
          to="/"
          className="font-display lowercase tracking-tighter text-2xl md:text-3xl px-4 md:px-8 py-3 hover:text-[#f5341c] transition-colors duration-150"
        >
          ← murkz presents
        </Link>
      </nav>
      <div className="px-4 md:px-8 py-14 md:py-20 max-w-3xl">
        <h1 className="font-display lowercase leading-[0.9] tracking-[-0.02em] text-5xl md:text-7xl">
          stats<span className="text-[#f5341c]">.</span>
        </h1>
        {!data ? (
          <form
            data-testid="stats-key-form"
            onSubmit={(e) => {
              e.preventDefault();
              load(key);
            }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <input
              data-testid="stats-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="STATS KEY"
              className="flex-1 bg-white border-4 border-[#0f0f0f] px-4 py-3 text-base font-bold uppercase tracking-widest placeholder-[#0f0f0f]/40 focus:outline-none focus:border-[#f5341c] rounded-none"
            />
            <button
              data-testid="stats-key-submit"
              type="submit"
              className="bg-[#0f0f0f] text-[#f4f4f0] font-display lowercase tracking-tight text-2xl px-8 py-3 border-4 border-[#0f0f0f] hover:bg-[#f5341c] hover:text-[#0f0f0f] transition-colors duration-150"
            >
              open stats
            </button>
          </form>
        ) : (
          <div className="mt-10 space-y-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: "visits", value: data.pageviews_total, tid: "stats-pageviews" },
                { label: "buy clicks", value: data.buy_clicks_total, tid: "stats-ticket-clicks" },
                { label: "conversion", value: `${data.conversion_pct}%`, tid: "stats-conversion" },
                { label: "sign ups", value: data.signups_total, tid: "stats-signups" },
              ].map((c) => (
                <div key={c.label} data-testid={c.tid} className="border-4 border-[#0f0f0f] p-4 bg-white">
                  <p className="font-display lowercase tracking-tight text-4xl md:text-5xl">{c.value}</p>
                  <p className="text-xs font-extrabold uppercase tracking-widest mt-1">{c.label}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-extrabold uppercase tracking-widest mb-1">Ticket funnel</p>
              <p className="text-xs font-medium mb-3 text-[#0f0f0f]/60">How visits turn into buy clicks — the percentage is the number to watch.</p>
              <ul className="space-y-1 text-sm font-medium">
                <li className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                  <span>visits</span>
                  <span className="font-bold">{data.pageviews_total}</span>
                </li>
                <li className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                  <span>scrolled to the ticket section</span>
                  <span className="font-bold">{data.views["tickets-section"] || 0}</span>
                </li>
                <li className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                  <span>clicked to buy</span>
                  <span className="font-bold">{data.buy_clicks_total} — {data.conversion_pct}%</span>
                </li>
                <li className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                  <span className="text-[#0f0f0f]/60">of which: hero / bottom bar / menu</span>
                  <span className="font-bold">
                    {data.clicks["buy-tickets-hero"] || 0} / {data.clicks["buy-tickets-sticky"] || 0} / {data.clicks["buy-tickets-menu"] || 0}
                  </span>
                </li>
                <li className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                  <span>interacted with the ticket box</span>
                  <span className="font-bold">{data.clicks["ticket-box-engaged"] || 0}</span>
                </li>
                <li className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                  <span>avg. time on tickets</span>
                  <span className="font-bold">{data.avg_time_on_tickets_s}s</span>
                </li>
                <li className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                  <span>table package dm clicks</span>
                  <span className="font-bold">{data.clicks["dm-tables"] || 0}</span>
                </li>
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div data-testid="stats-devices" className="border-4 border-[#0f0f0f] p-4 bg-white">
                <p className="text-xs font-extrabold uppercase tracking-widest mb-2">Devices</p>
                <p className="text-sm font-medium">mobile — {data.devices.mobile || 0}</p>
                <p className="text-sm font-medium">desktop — {data.devices.desktop || 0}</p>
              </div>
              <div data-testid="stats-visitors" className="border-4 border-[#0f0f0f] p-4 bg-white">
                <p className="text-xs font-extrabold uppercase tracking-widest mb-2">Visitors</p>
                <p className="text-sm font-medium">new — {data.visitors.new || 0}</p>
                <p className="text-sm font-medium">returning — {data.visitors.returning || 0}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-extrabold uppercase tracking-widest mb-1">Visits per day (last 14 days)</p>
              <p className="text-xs font-medium mb-3 text-[#0f0f0f]/60">Each row is one day. The red bar grows with how many visits that day had; the number on the right is the exact count. Days with zero visits show an empty bar.</p>
              <div className="space-y-1">
                {days.map(([day, v]) => (
                  <div key={day} className="flex items-center gap-3">
                    <span className="text-xs font-bold tracking-widest w-20 shrink-0">{day}</span>
                    <div className={`h-4 ${v > 0 ? "bg-[#f5341c]" : "bg-[#0f0f0f]/10"}`} style={{ width: `${Math.max((v / maxDay) * 100, 1.5)}%` }} />
                    <span className="text-xs font-bold">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-extrabold uppercase tracking-widest mb-3">Mailing list sign ups</p>
              {data.signups.length > 0 ? (
                <ul className="space-y-1 text-sm font-medium">
                  {data.signups.map((s, i) => (
                    <li key={i} data-testid={`stats-signup-${i}`} className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                      <span className="truncate">{s.email}</span>
                      <span className="shrink-0">{s.city}</span>
                      <span className="font-bold shrink-0">{s.ts}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm font-medium">No sign ups yet — when someone joins the mailing list, their email and city appear here.</p>
              )}
            </div>
            <div>
              <p className="text-sm font-extrabold uppercase tracking-widest mb-3">Top referrers</p>
              {Object.keys(data.top_referrers).length > 0 ? (
                <ul className="space-y-1 text-sm font-medium">
                  {Object.entries(data.top_referrers).map(([r, v]) => (
                    <li key={r} className="flex justify-between gap-4 border-b-2 border-[#0f0f0f]/10 py-1">
                      <span className="truncate">{r}</span>
                      <span className="font-bold shrink-0">{v}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm font-medium">None yet — this fills in as people arrive from Instagram, Google, etc.</p>
              )}
            </div>
          </div>
        )}
        {err && <p data-testid="stats-error" className="mt-4 text-sm font-bold text-[#f5341c] uppercase tracking-widest">{err}</p>}
      </div>
    </main>
  );
};

function App() {
  return (
    <div className="App">
      <div className="grain-overlay" aria-hidden="true" />
      <Toaster position="bottom-center" />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/stats" element={<Stats />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
